import {ChangeDetectionStrategy, Component, computed, inject, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {UserHttpService} from '../../../../selfservice/x-http-requests/user-http.service';
import {
  describeEmployeeIdentificationError
} from '../../../../_shared/errors/employee-identification-errors';

/**
 * Employee self-service control to set or change <b>their own</b> 6-digit critical-step-up PIN. It first loads the
 * employee's identification status and renders <i>nothing</i> unless the employee is enabled for identification
 * (so the PIN UI only appears for staff allowed to perform critical actions). When a PIN already exists it asks for
 * the current one (proof of knowledge) before allowing a change. The PIN is sent once to be hashed server-side and
 * is never stored client-side. Reuses the documented form-input / button / alert patterns; sits beside
 * change-password in the panel Settings.
 */
@Component({
  selector: 'app-employee-pin-self',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './employee-pin-self.component.html',
  styleUrl: './employee-pin-self.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmployeePinSelfComponent implements OnInit {
  private readonly userHttp = inject(UserHttpService);

  readonly loaded = signal(false);
  readonly enabled = signal(false);
  readonly hasPin = signal(false);

  readonly current = signal('');
  readonly pin = signal('');
  readonly confirm = signal('');
  readonly saving = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly successMessage = signal<string | null>(null);

  /** Exactly six ASCII digits — mirrors the backend policy (PinExtensions.IsValidPin). */
  readonly pinValid = computed(() => /^[0-9]{6}$/.test(this.pin()));
  readonly confirmMatches = computed(() => this.pin() === this.confirm());
  /** The current PIN is only required when one already exists. */
  readonly currentProvided = computed(() => !this.hasPin() || /^[0-9]{6}$/.test(this.current()));
  readonly canSave = computed(() =>
    this.pinValid() && this.confirmMatches() && this.currentProvided() && !this.saving());

  readonly validationHint = computed<string | null>(() => {
    if (this.pin().length === 0) return null;
    if (!this.pinValid()) return 'Die PIN muss aus genau 6 Ziffern bestehen.';
    if (this.confirm().length > 0 && !this.confirmMatches()) return 'Die PINs stimmen nicht überein.';
    return null;
  });

  ngOnInit(): void {
    this.userHttp.GetIdentificationStatus().subscribe({
      next: (s) => {
        this.enabled.set(s.enabled);
        this.hasPin.set(s.hasPin);
        this.loaded.set(true);
      },
      error: () => this.loaded.set(true)
    });
  }

  onCurrentInput(value: string): void {
    this.resetMessages();
    this.current.set(value.replace(/\D/g, '').slice(0, 6));
  }

  onPinInput(value: string): void {
    this.resetMessages();
    this.pin.set(value.replace(/\D/g, '').slice(0, 6));
  }

  onConfirmInput(value: string): void {
    this.resetMessages();
    this.confirm.set(value.replace(/\D/g, '').slice(0, 6));
  }

  save(): void {
    if (!this.canSave()) return;
    this.saving.set(true);
    this.resetMessages();

    this.userHttp.SetOwnPin(this.hasPin() ? this.current() : null, this.pin()).subscribe({
      next: () => {
        this.saving.set(false);
        this.hasPin.set(true);
        this.current.set('');
        this.pin.set('');
        this.confirm.set('');
        this.successMessage.set('PIN gespeichert.');
      },
      error: (err) => {
        this.saving.set(false);
        // German mapped from `ApiError.code`, never `description`. The two refusals somebody actually meets
        // while choosing a PIN — too weak, wrong current PIN — used to arrive here in English.
        this.errorMessage.set(
          describeEmployeeIdentificationError(err, 'Die PIN konnte nicht gespeichert werden.'));
      }
    });
  }

  private resetMessages(): void {
    this.errorMessage.set(null);
    this.successMessage.set(null);
  }
}
